import React, { useCallback, useEffect, useState } from 'react';
import { Eye, Pencil } from 'lucide-react';
import { adminApi } from '../../api/adminApi';
import AustralianPhoneInput from '../../components/AustralianPhoneInput';
import Avatar from '../../components/Avatar';
import Modal from '../../components/Modal';
import UserDetailsModal from '../../components/UserDetailsModal';
import { AdminLayout } from '../../components/layouts';
import {
  Alert,
  Badge,
  Button,
  EmptyState,
  Field,
  Input,
  Select,
  TabButton,
  TableShell,
  Tabs,
  Toast,
  tableClassName,
  tdClassName,
  thClassName,
} from '../../components/ui';

const PAGE_SIZES = [10, 25, 50];

const STATUS_VARIANT = {
  active: 'success',
  suspended: 'danger',
  deleted: 'danger',
};

const VERIFICATION_VARIANT = {
  approved: 'success',
  pending: 'warning',
  rejected: 'danger',
};

const VERIFICATION_LABEL = {
  approved: 'Verified',
  pending: 'Pending',
  rejected: 'Rejected',
};

const fmtDate = (value) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });
};

const AdminUsers = () => {
  const [role, setRole] = useState('all');
  const [status, setStatus] = useState('all');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [users, setUsers] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, limit: 10, total: 0, totalPages: 1 });
  const [counts, setCounts] = useState({ clients: 0, providers: 0, total: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [toast, setToast] = useState('');
  const [detailUser, setDetailUser] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [editForm, setEditForm] = useState({ name: '', phone: '', suburb: '', state: 'QLD', postcode: '', status: 'active' });
  const [editSaving, setEditSaving] = useState(false);
  const [editError, setEditError] = useState('');
  const [searchInput, setSearchInput] = useState('');

  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(searchInput);
      setPage(1);
    }, 350);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const response = await adminApi.listUsers({ role, status, search, page, limit });
      setUsers(response.data || []);
      setPagination(response.pagination || { page: 1, limit, total: 0, totalPages: 1 });
      setCounts(response.counts || { clients: 0, providers: 0, total: 0 });
    } catch (err) {
      setError(err.message || 'Failed to load users.');
    } finally {
      setLoading(false);
    }
  }, [role, status, search, page, limit]);

  useEffect(() => { load(); }, [load]);

  const flashToast = (message) => {
    setToast(message);
    setTimeout(() => setToast(''), 2500);
  };

  const openDetail = async (userId) => {
    setDetailLoading(true);
    try {
      const response = await adminApi.getUserDetail(userId);
      setDetailUser(response.data);
    } catch (err) {
      setError(err.message || 'Failed to load user.');
    } finally {
      setDetailLoading(false);
    }
  };

  const openEdit = (user) => {
    setEditError('');
    setEditUser(user);
    setEditForm({
      name: user.name || '',
      phone: user.phone || '',
      suburb: user.location?.suburb || '',
      state: user.location?.state || 'QLD',
      postcode: user.location?.postcode || '',
      status: user.status || 'active',
    });
  };

  const saveEdit = async (event) => {
    event.preventDefault();
    if (!editUser?._id) return;
    setEditError('');
    setEditSaving(true);
    try {
      await adminApi.updateUser(editUser._id, {
        name: editForm.name,
        phone: editForm.phone,
        location: { suburb: editForm.suburb, state: editForm.state, postcode: editForm.postcode },
        status: editForm.status,
      });
      await load();
      setEditUser(null);
      flashToast('User updated.');
    } catch (err) {
      setEditError(err.message || 'Failed to update user.');
    } finally {
      setEditSaving(false);
    }
  };

  const locationDisplay = (user) => {
    const location = user.location || {};
    const parts = [location.suburb, location.state, location.postcode].filter(Boolean);
    return parts.length ? parts.join(' ') : '—';
  };

  const onChangeRole = (nextRole) => { setRole(nextRole); setPage(1); };
  const onChangeStatus = (nextStatus) => { setStatus(nextStatus); setPage(1); };
  const onChangeLimit = (nextLimit) => { setLimit(nextLimit); setPage(1); };

  const buildPageNumbers = () => {
    const totalPages = pagination.totalPages || 1;
    const current = pagination.page || 1;
    const pages = [1];
    for (let number = current - 1; number <= current + 1; number += 1) {
      if (number > 1 && number < totalPages) pages.push(number);
    }
    if (totalPages > 1) pages.push(totalPages);
    const output = [];
    for (let index = 0; index < pages.length; index += 1) {
      output.push(pages[index]);
      if (index < pages.length - 1 && pages[index + 1] - pages[index] > 1) output.push('…');
    }
    return output;
  };

  const showingFrom = users.length === 0 ? 0 : (pagination.page - 1) * pagination.limit + 1;
  const showingTo = (pagination.page - 1) * pagination.limit + users.length;

  return (
    <AdminLayout title="User Management">
      <div className="mb-6">
        <h2 className="m-0 font-nunito text-[24px] font-black tracking-[-0.025em] text-giggs-ink sm:text-[28px]">Users</h2>
        <p className="mb-0 mt-1 text-sm leading-6 text-giggs-muted">Manage client and Gigger accounts from one place.</p>
      </div>

      {toast && <Toast>{toast}</Toast>}
      {error && <Alert variant="danger" className="mb-4">{error}</Alert>}

      <div className="mb-5 flex flex-col gap-4 xl:flex-row xl:items-center xl:justify-between">
        <div className="flex min-w-0 flex-1 flex-col gap-3 lg:flex-row lg:flex-wrap lg:items-center">
          <Input
            className="lg:max-w-[330px]"
            placeholder="Search by name or email…"
            value={searchInput}
            onChange={(event) => setSearchInput(event.target.value)}
          />

          <Tabs className="w-fit max-w-full overflow-x-auto">
            {[
              { value: 'all', label: 'All', count: counts.total },
              { value: 'client', label: 'Clients', count: counts.clients },
              { value: 'provider', label: 'Giggers', count: counts.providers },
            ].map((option) => (
              <TabButton key={option.value} active={role === option.value} count={option.count} onClick={() => onChangeRole(option.value)}>
                {option.label}
              </TabButton>
            ))}
          </Tabs>

          <Select className="lg:w-auto lg:min-w-[150px]" value={status} onChange={(event) => onChangeStatus(event.target.value)}>
            <option value="all">All statuses</option>
            <option value="active">Active</option>
            <option value="suspended">Suspended</option>
          </Select>
        </div>

        <div className="flex items-center gap-2 text-xs font-semibold text-giggs-muted">
          <span>Show</span>
          <Select className="!w-[76px] !py-2" value={limit} onChange={(event) => onChangeLimit(Number(event.target.value))}>
            {PAGE_SIZES.map((size) => <option key={size} value={size}>{size}</option>)}
          </Select>
          <span>per page</span>
        </div>
      </div>

      <TableShell>
        <table className={`${tableClassName} min-w-[940px]`}>
          <thead>
            <tr>
              {['User', 'Email', 'Role', 'Status', 'Verification', 'Location', 'Joined'].map((heading) => <th key={heading} className={thClassName}>{heading}</th>)}
              <th className={`${thClassName} text-right`}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan="8" className="px-4 py-10"><EmptyState title="Loading users…" /></td></tr>
            ) : users.length === 0 ? (
              <tr><td colSpan="8" className="px-4 py-10"><EmptyState title="No users found" description="Try adjusting your search or filters." /></td></tr>
            ) : users.map((user) => {
              const isProvider = user.role === 'provider';
              return (
                <tr key={user._id} className="hover:bg-[#F8FAF9]">
                  <td className={tdClassName}>
                    <div className="flex items-center gap-3">
                      <Avatar user={user} size={36} gradient={isProvider ? 'linear-gradient(135deg, #C7E8D4, #AAF0C4)' : 'linear-gradient(135deg, #D3F9E0, #AAF0C4)'} />
                      <span className="font-bold text-giggs-ink">{user.name}</span>
                    </div>
                  </td>
                  <td className={`${tdClassName} max-w-[240px] truncate text-[13px]`}>{user.email}</td>
                  <td className={tdClassName}><Badge variant={isProvider ? 'green' : 'info'}>{isProvider ? 'Gigger' : 'Client'}</Badge></td>
                  <td className={tdClassName}><Badge variant={STATUS_VARIANT[user.status] || 'success'}>{user.status ? user.status.charAt(0).toUpperCase() + user.status.slice(1) : 'Active'}</Badge></td>
                  <td className={tdClassName}>
                    {isProvider ? <Badge variant={VERIFICATION_VARIANT[user.verificationStatus] || 'warning'}>{VERIFICATION_LABEL[user.verificationStatus] || 'Pending'}</Badge> : <span className="text-xs text-giggs-faint">—</span>}
                  </td>
                  <td className={`${tdClassName} whitespace-nowrap text-xs text-giggs-muted`}>{locationDisplay(user)}</td>
                  <td className={`${tdClassName} whitespace-nowrap text-xs text-giggs-muted`}>{fmtDate(user.createdAt)}</td>
                  <td className={`${tdClassName} text-right`}>
                    <div className="inline-flex gap-1.5">
                      <Button variant="ghost" size="icon" className="!h-9 !w-9 text-giggs-muted hover:text-giggs-green" title="View full details" onClick={() => openDetail(user._id)} disabled={detailLoading}>
                        <Eye size={16} />
                      </Button>
                      <Button variant="ghost" size="icon" className="!h-9 !w-9 text-giggs-muted hover:text-giggs-green" title="Edit account info" onClick={() => openEdit(user)}>
                        <Pencil size={16} />
                      </Button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>

        <div className="flex flex-col gap-3 bg-[#FBFCFB] px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
          <span className="text-xs font-medium text-giggs-muted">{pagination.total === 0 ? 'No results' : `Showing ${showingFrom}–${showingTo} of ${pagination.total} users`}</span>
          <div className="flex flex-wrap items-center gap-1.5 sm:justify-end">
            <Button variant="outline" size="sm" onClick={() => setPage((current) => Math.max(1, current - 1))} disabled={pagination.page <= 1 || loading}>‹ Prev</Button>
            {buildPageNumbers().map((number, index) => number === '…' ? <span key={`gap-${index}`} className="px-1 text-[13px] text-giggs-muted">…</span> : (
              <Button
                variant={number === pagination.page ? 'primary' : 'outline'}
                size="sm"
                className="min-w-9 px-2.5"
                key={number}
                onClick={() => setPage(number)}
                disabled={loading}
              >
                {number}
              </Button>
            ))}
            <Button variant="outline" size="sm" onClick={() => setPage((current) => Math.min(pagination.totalPages, current + 1))} disabled={pagination.page >= pagination.totalPages || loading}>Next ›</Button>
          </div>
        </div>
      </TableShell>

      {detailUser && <UserDetailsModal user={detailUser} role={detailUser.role === 'provider' ? 'provider' : 'client'} title={`${detailUser.role === 'provider' ? 'Gigger' : 'Client'} — Full Details`} onClose={() => setDetailUser(null)} />}

      {editUser && (
        <Modal title={`Edit ${editUser.role === 'provider' ? 'Gigger' : 'Client'}`} onClose={() => !editSaving && setEditUser(null)}>
          <form onSubmit={saveEdit} className="flex flex-col gap-5 px-5 pb-6 pt-3 sm:px-8 sm:pb-8">
            {editError && <Alert variant="danger">{editError}</Alert>}

            <div>
              <h3 className="mb-3 mt-0 font-nunito text-sm font-extrabold text-giggs-ink">Personal information</h3>
              <div className="grid gap-4 md:grid-cols-2">
                <Field label="Full Name" required>
                  <Input value={editForm.name} onChange={(event) => setEditForm({ ...editForm, name: event.target.value })} required />
                </Field>
                <Field label="Phone" required>
                  <AustralianPhoneInput value={editForm.phone} onChange={(phone) => setEditForm({ ...editForm, phone })} required />
                </Field>
              </div>
              <Field label="Email" hint="Email cannot be edited from this screen." className="mt-4">
                <Input value={editUser.email || ''} disabled />
              </Field>
            </div>

            <div>
              <h3 className="mb-3 mt-0 font-nunito text-sm font-extrabold text-giggs-ink">Location</h3>
              <div className="grid gap-4 md:grid-cols-[2fr_1fr_1fr]">
                <Field label="Suburb"><Input value={editForm.suburb} onChange={(event) => setEditForm({ ...editForm, suburb: event.target.value })} /></Field>
                <Field label="State">
                  <Select value={editForm.state} onChange={(event) => setEditForm({ ...editForm, state: event.target.value })}>
                    <option>QLD</option><option>NSW</option><option>VIC</option><option>SA</option><option>WA</option><option>TAS</option><option>NT</option><option>ACT</option>
                  </Select>
                </Field>
                <Field label="Postcode"><Input maxLength="4" value={editForm.postcode} onChange={(event) => setEditForm({ ...editForm, postcode: event.target.value })} /></Field>
              </div>
            </div>

            <Field label="Account Status" hint={editForm.status === 'suspended' ? 'Suspended users cannot log in. Verification approval/rejection is managed from the Verifications page.' : undefined}>
              <Select value={editForm.status} onChange={(event) => setEditForm({ ...editForm, status: event.target.value })}>
                <option value="active">Active</option>
                <option value="suspended">Suspended</option>
              </Select>
            </Field>

            <div className="mt-1 flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
              <Button variant="secondary" onClick={() => setEditUser(null)} disabled={editSaving}>Cancel</Button>
              <Button type="submit" disabled={editSaving}>{editSaving ? 'Saving…' : 'Save Changes'}</Button>
            </div>
          </form>
        </Modal>
      )}
    </AdminLayout>
  );
};

export default AdminUsers;
